import type { PriceData } from '@libs/common-types';
import { modifyStringReplaceAll, removeSpecialCharacters } from '@libs/core';

import {
    PRODUCT_VARIATION_TYPES,
    type ProductCategories,
    type ProductCategory,
    type ProductHit,
    type ProductHitPrice,
    type ProductSize,
    type ProductVariation,
    type ProductVariationType,
} from '../types';
import { decodeEntityString } from './decodeEntityString';

export const computeVariationDisplayStrings = (
    variationData?: ProductVariation[],
    legacyVariations?: string[]
): string[] => {
    if (!variationData?.length) return legacyVariations ?? [];

    const uniqueValuesByType = {} as Record<ProductVariationType, Set<string>>;
    for (const type of PRODUCT_VARIATION_TYPES) {
        uniqueValuesByType[type] = new Set();
    }

    for (const { type, value, onlineFrom } of variationData) {
        if (isVariationOnline(onlineFrom)) {
            uniqueValuesByType[type].add(value);
        }
    }

    const result: string[] = [];
    for (const type of PRODUCT_VARIATION_TYPES) {
        const uniqueCount = uniqueValuesByType[type].size;
        if (uniqueCount > 0) {
            const label = uniqueCount === 1 ? type : `${type}s`;
            result.push(`${uniqueCount} ${label}`);
        }
    }
    return result;
};

const isVariationOnline = (onlineFrom: number | null) => {
    return onlineFrom === null || (Number.isFinite(onlineFrom) && onlineFrom <= Date.now());
};

export const isPriceData = (price: ProductHitPrice | PriceData): price is PriceData => 'current' in price;

export const formatPrice = (price: string) => price.replace(/\$/g, '').trim();

const isCollapsedPriceRange = (price: PriceData) => price.listRange?.length === 1 || price.saleRange?.length === 1;

export const isPriceRange = (price?: ProductHitPrice | PriceData): boolean => {
    if (!price) return false;

    if (isPriceData(price)) return price.listRange?.length === 2 || price.saleRange?.length === 2;

    if (!price?.formatted?.primary) return false;

    const isMarkedAsRange = price.displayType === 'range';
    const primaryHasHyphen = price.formatted.primary.includes('-');
    const strikethroughHasHyphen = price?.formatted?.strikethrough?.includes('-') ?? true;
    return isMarkedAsRange && primaryHasHyphen && strikethroughHasHyphen;
};

export const getPriceVariant = (price?: ProductHitPrice | PriceData): 'primary' | 'disabled' | 'sale' => {
    if (!price) return 'primary';

    if (isPriceData(price)) return price.sale || price.saleRange ? 'disabled' : 'primary';

    return price?.formatted?.strikethrough ? 'disabled' : 'primary';
};

const getMinPrices = (price: ProductHitPrice) => {
    if (price.formatted.primary && !price.formatted.strikethrough) {
        const minPrimary = price.formatted.primary.split('-')[0];

        return [formatPrice(minPrimary)];
    }

    if (price.formatted.primary && price.formatted.strikethrough) {
        const minPrimary = price.formatted.primary.split('-')[0];
        const minStrikethrough = price.formatted.strikethrough.split('-')[0];

        if (minPrimary === minStrikethrough) return [formatPrice(minStrikethrough)];

        return [formatPrice(minPrimary), formatPrice(minStrikethrough)];
    }

    return [0];
};

export const getRangePrices = (price: ProductHitPrice) => {
    if (price.formatted.primary && !price.formatted.strikethrough) {
        const firstPrice = price.formatted.primary.split('-');

        return [formatPrice(firstPrice[0]), formatPrice(firstPrice[1])];
    }

    if (price.formatted.primary && price.formatted.strikethrough) {
        const firstPrice = price.formatted.primary.split('-');
        const secondPrice = price.formatted.strikethrough.split('-');

        return [
            formatPrice(firstPrice[0]),
            formatPrice(firstPrice[1]),
            formatPrice(secondPrice[0]),
            formatPrice(secondPrice[1]),
        ];
    }

    return [0];
};

export const getNonRangePrices = (price: ProductHitPrice) => {
    if (price.formatted.primary && !price.formatted.strikethrough) {
        return [formatPrice(price.formatted.primary)];
    }

    if (price.formatted.primary && price.formatted.strikethrough) {
        return [formatPrice(price.formatted.primary), formatPrice(price.formatted.strikethrough)];
    }

    return [0];
};

export const getOptionsFromPrice = (price: ProductHitPrice | PriceData | undefined) => {
    if (!price) return;

    let prices;
    let isLowestPriceSale;

    if (isPriceData(price)) {
        const minList = price.listRange?.[0];
        const minSale = price.saleRange?.[0];

        isLowestPriceSale = price.optionsFromList !== undefined;

        const listPrice = [price.optionsFromList ?? minList ?? price.list];
        const salePrice = isLowestPriceSale && minSale !== undefined ? [minSale] : [];

        prices = [...salePrice, ...listPrice];
    } else {
        prices = getMinPrices(price);
        isLowestPriceSale = prices.length === 2;
    }

    return {
        prices,
        priceVariant: isLowestPriceSale ? 'disabled' : 'primary',
    };
};

export const getPrices = (price: ProductHitPrice | PriceData | undefined, isRange: boolean): Array<string | number> => {
    if (!price) return [0];

    if (isPriceData(price)) {
        if (isRange || isCollapsedPriceRange(price)) {
            const listPrices = price.listRange ?? [price.list, price.list];
            const salePrices = price.saleRange ?? [];

            return [...salePrices, ...listPrices];
        }

        if (price.sale) return [price.sale, price.list];

        return [price.list];
    }

    if (isRange) {
        return getRangePrices(price);
    }

    return getNonRangePrices(price);
};

export const getVariants = (variations?: string[], variationData?: ProductVariation[]): string => {
    const displayStrings = computeVariationDisplayStrings(variationData, variations);

    if (!displayStrings.length) {
        return ' ';
    }

    return displayStrings.join(' ').toLowerCase();
};

export const getReviewCount = (reviewCount?: number) => {
    if (reviewCount) {
        return `(${reviewCount})`;
    }

    return '(0)';
};

export const getPrimaryCategoryList = (
    primaryCategoryId?: string,
    categories?: ProductCategories
): ProductCategory[] => {
    if (!primaryCategoryId || !categories) {
        return [];
    }

    let categoriesToUse: ProductCategory[] = [];

    // find which list of categories is the primary one
    categories.forEach((subCategories: ProductCategory[]) => {
        subCategories.forEach((category: ProductCategory) => {
            if (category.id === primaryCategoryId) {
                categoriesToUse = subCategories;
            }
        });
    });

    return categoriesToUse;
};

export const getItemSize = (size: ProductSize) => {
    let itemSize = '';
    for (const key in size) {
        itemSize += size[key as keyof ProductSize];
    }
    return itemSize;
};

export const toUrlString = (url: string) => {
    const slug = modifyStringReplaceAll(removeSpecialCharacters(url, ['&', ' ', ' - ']), {
        '&': 'and',
        ' - ': '---', // we need to keep --- in url to match legacy URLs
        '@': '',
        ' ': '-',
        ',': '',
        ';': '',
    });
    return encodeURI(slug).toLocaleLowerCase();
};

export const categoryPath = (category: string) =>
    modifyStringReplaceAll(category, {
        '&': 'and',
        '@': '',
    }).toLocaleLowerCase();

export const getProductUrl = (product: ProductHit, useSkuId = false) => {
    const categories = getPrimaryCategoryList(product.primary_category_id, product.categories);

    // slice() ensures a new array is made so that we are not mutating
    // it with reverse() was causing a hydration issue
    let primaryCategories = categories.slice();

    // Category array might sometimes come in order and other times
    // it might be reversed. The primary category should always be the
    // last element in the category hierarchy, so the array should
    // only be reversed if the primary category is found at the first index.
    if (primaryCategories.at(0)?.id === product.primary_category_id) {
        primaryCategories = primaryCategories.reverse();
    }

    const categoryHierarchy = primaryCategories.reduce(
        (acc: string, category: ProductCategory) => `${acc}/${categoryPath(toUrlString(category.name.default))}`,
        ''
    );

    const productName = toUrlString(product?.name || '');
    const productIdOrSku = useSkuId ? product.id : product.masterProductID;
    return `${categoryHierarchy}/${productName}-${productIdOrSku}.html`;
};

export const isNumericSKU = (productCode: string) => {
    // Regular expression to match a SKU: exactly 7 digits
    const skuRegex = /^\d{7}$/;
    return skuRegex.test(productCode);
};

export const transformHitForProductTile = (productHit: ProductHit, isOptionsFrom = false) => {
    const hrefFormat = getProductUrl(productHit);
    const {
        id,
        name,
        short_description: shortDescription,
        images,
        price: legacyPrice,
        priceData,
        bvAverageRating,
        bvReviewCount,
        badges,
        brand,
        masterProductID,
        variations,
        variationData,
        merchandisingHierarchy,
        __position,
        size,
        flavor,
        color,
        eligiblePromotions,
    } = productHit;

    const price = priceData ?? legacyPrice;
    const isRange = isPriceRange(price);
    const meta = getVariants(variations, variationData);
    const possibleCombinations = (meta ?? '')
        .match(/\d+/g)
        ?.map(num => parseInt(num, 10))
        .reduce((total, currentValue) => total * currentValue);

    const displayMeta = possibleCombinations && possibleCombinations > 1;
    const shouldUseOptionsFromPrice = isOptionsFrom && isRange;
    const optionsFrom = shouldUseOptionsFromPrice ? getOptionsFromPrice(price) : undefined;
    const prices = optionsFrom?.prices ?? getPrices(price, isRange);
    const priceVariant = optionsFrom?.priceVariant ?? getPriceVariant(price);

    return {
        _snippetResult: undefined,
        __position,
        id,
        brand,
        masterProductID,
        categoryList: getPrimaryCategoryList(productHit.primary_category_id, productHit.categories),
        objectID: `${id}`,
        title: decodeEntityString(name),
        description: shortDescription,
        href: hrefFormat,
        target: '_self',
        imgSrc: images?.large,
        imgAlt: name,
        priceVariant,
        prices,
        isPriceRange: shouldUseOptionsFromPrice ? false : isRange,
        showOptionsFrom: shouldUseOptionsFromPrice,
        outOfStock: false,
        // this gets added from getProductsPromotions because of a needed call to SFCC
        promos: undefined,
        rating: bvAverageRating,
        ratingLabel: getReviewCount(bvReviewCount),
        badgeText: badges && badges.length > 0 ? badges[0] : undefined,
        badgeVariant: 'info',
        meta: displayMeta ? meta : '',
        rawProductHit: productHit,
        merchandisingHierarchy,
        itemSize: getItemSize(size as ProductSize),
        flavor,
        color,
        eligiblePromotions,
    };
};
